import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { FormGroup } from 'shared-components';
import styles from './user.module.css';
import { useGetUserQuery, useUpdateUserMutation } from './users.api';
import * as yup from 'yup';
import { useAppSelector } from '../../app/hooks';
import { toast } from 'react-toastify';
import { IEditingUser, IUserSessionPayload, Role } from '@topshots/core-models';
import { selectTokenPayload } from '../login/authSlice';
import { FormLayout } from 'shared-components';
import { Label } from 'shared-components';
import { Input } from 'shared-components';
import { Select } from 'shared-components';
import { CheckboxList } from 'shared-components';
import { Checkbox } from 'shared-components';
import { Card, CardContent } from 'shared-components';
import { PageTitle } from 'shared-components';
import Routes from '../../app/routes';

export const User = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetUserQuery(id as string);
  const { accountId: currentAccountId } = useAppSelector(
    selectTokenPayload
  ) as IUserSessionPayload;
  const { errors, handleSubmit, getFieldProps, submitForm, ...formik } =
    useFormik<IEditingUser>({
      initialValues: {
        ...data,
        roles:
          data?.accounts?.find((a) => a.account_id === currentAccountId)
            ?.roles ?? [],
      } as IEditingUser,
      validationSchema: yup.object({
        firstName: yup.string().required(),
        lastName: yup.string().required(),
        email: yup.string().required().email(),
        phone: yup.string(),
      }),
      enableReinitialize: true,
      onSubmit: async (values) => {
        const { firstName, lastName, email, phone, auth_level, roles } = values;
        await updateUser({
          id: id as string,
          data: {
            firstName,
            lastName,
            email,
            phone,
            auth_level,
            roles,
          },
        });
        toast.success('User updated!');
      },
    });

  const handleCheckboxChange = (item: Role) => {
    const { roles } = formik.values;
    if (roles.includes(item)) {
      // Remove item if it's already selected
      formik.setFieldValue(
        'roles',
        roles.filter((val) => val !== item)
      );
    } else {
      // Add item if it's not selected
      formik.setFieldValue('roles', [...roles, item]);
    }
  };

  const [updateUser] = useUpdateUserMutation();

  if (isLoading) {
    return <span>loading...</span>;
  }

  return (
    <>
      <PageTitle
        title={`User`}
        crumbs={[
          { display: Routes.DASHBOARD.title, link: Routes.DASHBOARD.link },
          { display: Routes.USERS.title, link: Routes.USERS.link },
          {
            display: `${data?.firstName} ${data?.lastName}`,
            link: Routes.USER.createLink?.(id as string) ?? '',
          },
        ]}
        allowSave={true}
        onAction={async (action) => {
          switch (action) {
            case 'save':
              submitForm();
              break;
          }
        }}
      />
      <Card className={styles.user}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <FormLayout className={styles.userForm}>
              <FormGroup>
                <Label htmlFor="firstName">First name</Label>
                <Input
                  {...getFieldProps('firstName')}
                  type="text"
                  id="firstName"
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  {...getFieldProps('lastName')}
                  type="text"
                  id="lastName"
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input {...getFieldProps('email')} type="email" id="email" />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="phone">Phone</Label>
                <Input {...getFieldProps('phone')} type="tel" id="phone" />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="auth_level">Auth level</Label>
                <Select {...getFieldProps('auth_level')} id="auth_level">
                  <option value="user">User</option>
                  <option value="uploader">Uploader</option>
                  <option value="admin">Admin</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <CheckboxList label="Roles" direction="horizontal">
                  {Object.entries(Role).map(([title, role]) => (
                    <Checkbox
                      label={title}
                      type="checkbox"
                      id={`Role:${role}`}
                      name="selectedItems"
                      value={role}
                      checked={formik.values.roles.includes(role)}
                      onChange={() => handleCheckboxChange(role)}
                    />
                  ))}
                </CheckboxList>

                <ul className={styles.roleList}></ul>
              </FormGroup>
            </FormLayout>
          </form>
        </CardContent>
      </Card>
    </>
  );
};
